import { z } from "zod";
import {
  EnrichedCompanyIdPublicSchema,
  CompanyEnrichmentRequestIdPublicSchema,
} from "./branded-id.schemas.js";

const enrichedCompanyLocationSchema = z.object({
  address: z.string(),
  isPrimary: z.boolean(),
  addressLine: z.string().nullable(),
  locality: z.string().nullable(),
  adminDistrict: z.string().nullable(),
  countryIso: z.string().nullable(),
  countryName: z.string().nullable(),
  postalCode: z.string().nullable(),
  neighborhood: z.string().nullable(),
});

const enrichedCompanyDerivedDataSchema = z.object({
  businessStage: z.string().nullable(),
  businessTypes: z.array(z.string()),
  aiDescription: z.string().nullable(),
  industries: z.array(z.string()),
  subindustries: z.array(z.string()),
  patternTags: z.string().nullable(),
  primaryOfferings: z.array(z.string()),
  revenueStreams: z.array(z.string()),
  scaleScope: z.string().nullable(),
});

export type EnrichedCompanyLocation = z.infer<
  typeof enrichedCompanyLocationSchema
>;

export type EnrichedCompanyDerivedData = z.infer<
  typeof enrichedCompanyDerivedDataSchema
>;

/**
 * Domain enriched-company model. Single source that the backend entity type
 * extends (adding storage-only fields such as the Mongo id, raw provider
 * payload, external ids, and request input) and that the admin transport view
 * projects via `.pick`, so entity, backend type, and view cannot drift apart.
 */
const _enrichedCompanySchema = z.object({
  idPublic: EnrichedCompanyIdPublicSchema,
  name: z.string(),
  domain: z.string().nullable(),
  industry: z.string().nullable(),
  sizeRange: z.string().nullable(),
  employeeCount: z.number().nullable(),
  headquarters: z.string().nullable(),
  locations: z.array(enrichedCompanyLocationSchema),
  description: z.string().nullable(),
  linkedInUrl: z.string().nullable(),
  linkedInOrgId: z.number().nullable(),
  websiteUrl: z.string().nullable(),
  logoUrl: z.string().nullable(),
  foundedYear: z.number().nullable(),
  annualRevenue: z.string().nullable(),
  totalFundingRange: z.string().nullable(),
  companyType: z.string().nullable(),
  followerCount: z.number().nullable(),
  country: z.string().nullable(),
  specialties: z.array(z.string()),
  derivedData: enrichedCompanyDerivedDataSchema.nullable(),
  enrichmentSource: z.string(),
  enrichmentConfidence: z.number(),
  enrichedAt: z.coerce.date(),
  enrichmentRequestIdPublic: CompanyEnrichmentRequestIdPublicSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const EnrichedCompanySchema = _enrichedCompanySchema.readonly();

export type EnrichedCompany = z.infer<typeof EnrichedCompanySchema>;
